import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { fetchAIPromptTemplates, previewAIPrompt } from '../../services/reportService';
import { buildAICommentPayload } from '../../utils/aiSummaryUtils';

const STORAGE_KEY = 'ga4_ai_prompt_prefs';

/** Load saved template preferences from localStorage */
function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { promptTemplate: 'executive', customInstructions: '' };
  } catch {
    return { promptTemplate: 'executive', customInstructions: '' };
  }
}

function savePrefs(prefs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

/**
 * AI comment settings dialog — template picker, custom instructions, prompt preview
 */
const AICommentSettingsDialog = ({ open, onClose, report, metricIndex = 0, onGenerate, generating }) => {
  const [templates, setTemplates] = useState([]);
  const [promptTemplate, setPromptTemplate] = useState('executive');
  const [customInstructions, setCustomInstructions] = useState('');
  const [promptPreview, setPromptPreview] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prefs = loadPrefs();
    setPromptTemplate(prefs.promptTemplate || 'executive');
    setCustomInstructions(prefs.customInstructions || '');

    fetchAIPromptTemplates()
      .then(setTemplates)
      .catch(() => {
        setTemplates([
          { id: 'executive', label: 'Executive Özet', description: 'Kısa yönetici özeti' },
          { id: 'detailed', label: 'Detaylı CRO Analizi', description: 'Derinlemesine analiz' },
        ]);
      });
  }, [open]);

  const payload = useMemo(
    () => (report ? buildAICommentPayload(report, metricIndex) : null),
    [report, metricIndex],
  );

  const selectedTemplate = templates.find((t) => t.id === promptTemplate);

  const refreshPreview = useCallback(async () => {
    if (!payload) return;
    setLoadingPreview(true);
    try {
      const text = await previewAIPrompt({
        ...payload,
        promptTemplate,
        customInstructions: customInstructions.trim(),
      });
      setPromptPreview(text);
    } catch {
      setPromptPreview('Prompt önizlemesi alınamadı.');
    } finally {
      setLoadingPreview(false);
    }
  }, [payload, promptTemplate, customInstructions]);

  useEffect(() => {
    if (!open || !payload) return;
    const timer = setTimeout(refreshPreview, 300);
    return () => clearTimeout(timer);
  }, [open, payload, promptTemplate, customInstructions, refreshPreview]);

  const handleGenerate = () => {
    const options = {
      promptTemplate,
      customInstructions: customInstructions.trim(),
    };
    savePrefs(options);
    onGenerate?.(options);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#240B4B' }}>
        AI Yorum Ayarları
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Şablon seçin, isteğe bağlı ek talimat ekleyin ve gönderilecek promptu inceleyin.
        </Typography>

        <TextField
          select
          fullWidth
          label="Yorum şablonu"
          value={promptTemplate}
          onChange={(e) => setPromptTemplate(e.target.value)}
          sx={{ mb: 1 }}
        >
          {templates.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>

        {selectedTemplate?.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {selectedTemplate.description}
          </Typography>
        )}

        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Ek talimatlar (isteğe bağlı)"
          placeholder="Örn: Mobil trafik ağırlıklı yorumla / Rakip kampanya dönemini dikkate al"
          value={customInstructions}
          onChange={(e) => setCustomInstructions(e.target.value)}
          inputProps={{ maxLength: 1200 }}
          sx={{ mb: 2 }}
        />

        <Accordion elevation={0} sx={{ border: '1px solid #eee', borderRadius: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Temel promptu görüntüle
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                maxHeight: 220,
                overflow: 'auto',
                fontSize: 12,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                bgcolor: '#fafafa',
                borderRadius: 1,
                border: '1px solid #eee',
              }}
            >
              {loadingPreview ? 'Yükleniyor...' : promptPreview || '—'}
            </Box>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={generating}>
          İptal
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={generating || !payload}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
          sx={{ bgcolor: '#240B4B', '&:hover': { bgcolor: '#3d1c6b' } }}
        >
          {generating ? 'Oluşturuluyor...' : 'Yorum Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AICommentSettingsDialog;
